/**
 * @file: useConstructionJobActions.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
/**
 * @file: useTaskHeaderOptions.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { usePrompt } from '@euler/functions/ux/usePrompt';
import { usePersistFn } from '@euler/utils/hooks';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { RefObject } from 'react';
import { findNodeHandle, View } from 'react-native';

type ActionItem = {
  type: 'add-procedure' | 'revert-all-changes' | 'cancel-job' | 'close';
  text: string;
};

const kItems: ActionItem[] = [
  { type: 'add-procedure', text: '添加工序' },
  { type: 'revert-all-changes', text: '撤销所有修改' },
  { type: 'cancel-job', text: '取消施工项目' },
  { type: 'close', text: '关闭' },
];

export const useConstructionJobActions = ({
  isDirty,
  onAddProcedure,
  onCancelConstructionJob,
  onUndoAllChanges,
}: {
  isDirty?: boolean;
  onAddProcedure?: (name: string) => void;
  onCancelConstructionJob?: () => void;
  onUndoAllChanges?: () => void;
}) => {
  const prompt = usePrompt();

  const { showActionSheetWithOptions } = useActionSheet();

  const showActions = usePersistFn((anchor?: RefObject<View>) => {
    let nodeId: number | null = null;
    if (anchor?.current != null) {
      nodeId = findNodeHandle(anchor.current);
    }

    const items = kItems.filter(
      x => x.type !== 'revert-all-changes' || isDirty,
    );

    showActionSheetWithOptions(
      {
        options: items.map(x => x.text),
        anchor: nodeId ?? undefined,
        cancelButtonIndex: items.findIndex(x => x.type === 'close'),
        destructiveButtonIndex: items.findIndex(x => x.type === 'cancel-job'),
      },
      async buttonIndex => {
        if (buttonIndex == null) return;
        const item = items[buttonIndex] as ActionItem | undefined;
        if (item?.type === 'add-procedure') {
          void prompt({
            title: '添加工序',
            message: '工序名称不能与已有工序名称重复',
            confirmButtonText: '确认',
            cancelButtonText: '取消',
          }).then((name: string | undefined) => {
            name = name?.trim();
            if (!name) return;
            onAddProcedure?.(name);
          });
        } else if (item?.type === 'revert-all-changes') {
          onUndoAllChanges?.();
        } else if (item?.type === 'cancel-job') {
          onCancelConstructionJob?.();
        }
      },
    );
  });

  return { showActions };
};
